import React from 'react';
import { Play, CheckCircle2, ArrowRight, Star, Shield } from 'lucide-react';

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-brand-500/30">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-slate-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-brand-400 to-brand-600 rounded-lg flex items-center justify-center">
              <span className="font-bold text-white">A</span>
            </div>
            <span className="font-bold text-lg tracking-tight">AcmeScale</span>
          </div>
          <button className="bg-white dark:bg-slate-900/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium transition-all">
            Login
          </button>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-400 text-xs font-medium mb-8 animate-fade-in-up">
            <Star className="w-3 h-3 fill-brand-400" />
            <span>Novo: Integração com Vercel & AWS</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-white mb-6 max-w-4xl leading-tight">
            Implante sem <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-cyan-300">Dores de Cabeça.</span><br />
            Escale sem Limites.
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mb-10 leading-relaxed">
            A infraestrutura de roteamento inteligente para times frontend modernos. 
            Automatize seu pipeline CI/CD e gerencie ambientes híbridos sem esforço.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 w-full justify-center mb-16">
            <button className="group relative px-8 py-4 bg-brand-600 hover:bg-brand-500 text-white rounded-full font-bold text-lg transition-all shadow-lg shadow-brand-500/25 flex items-center justify-center gap-2">
              Teste Grátis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="px-8 py-4 bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-slate-600 text-slate-300 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-2">
              Ver Documentação
            </button>
          </div>

          {/* Video Container */}
          <div className="w-full max-w-5xl relative group">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-brand-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            
            <div className="relative aspect-video bg-slate-900 rounded-xl border border-slate-800 overflow-hidden shadow-2xl flex items-center justify-center group-hover:border-slate-700 transition-colors">
              <div className="absolute inset-0 bg-[url('https://picsum.photos/1920/1080?grayscale&blur=2')] bg-cover bg-center opacity-40"></div>
              
              <button className="relative w-20 h-20 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full flex items-center justify-center border border-white/20 hover:scale-110 transition-all duration-300 group/btn">
                <Play className="w-8 h-8 text-white fill-white ml-1 group-hover/btn:text-brand-300 transition-colors" />
              </button>
              
              <div className="absolute bottom-6 left-6 flex items-center gap-3">
                 <div className="flex -space-x-2">
                    <img src="https://picsum.photos/40/40?random=1" className="w-8 h-8 rounded-full border-2 border-slate-900" alt="User" />
                    <img src="https://picsum.photos/40/40?random=2" className="w-8 h-8 rounded-full border-2 border-slate-900" alt="User" />
                    <img src="https://picsum.photos/40/40?random=3" className="w-8 h-8 rounded-full border-2 border-slate-900" alt="User" />
                 </div>
                 <p className="text-sm font-medium text-slate-300">Usado por +10.000 engenheiros</p>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="py-12 border-y border-white/5 bg-slate-900/50 mt-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-8">Confiado por times na</p>
            <div className="flex flex-wrap justify-center gap-12 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
               {/* Placeholders for logos */}
               <h3 className="text-xl font-bold text-white">ACME Corp</h3>
               <h3 className="text-xl font-bold text-white">Stark Ind</h3>
               <h3 className="text-xl font-bold text-white">Wayne Ent</h3>
               <h3 className="text-xl font-bold text-white">Cyberdyne</h3>
               <h3 className="text-xl font-bold text-white">Massive Dynamic</h3>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-6 h-6 text-brand-400" />,
                title: "Roteamento Híbrido",
                desc: "Alterna automaticamente entre estratégias de roteamento Hash e Browser com base na detecção de ambiente."
              },
              {
                icon: <CheckCircle2 className="w-6 h-6 text-brand-400" />,
                title: "Zero Config",
                desc: "Solte em qualquer Cloud IDE ou projeto Vercel e funciona. Sem mais erros 404 ao atualizar."
              },
              {
                icon: <Star className="w-6 h-6 text-brand-400" />,
                title: "Pronto para Produção",
                desc: "Totalmente otimizado para SEO, rastreamento UTM e análises quando implantado em domínios de produção."
              }
            ].map((feature, idx) => (
              <div key={idx} className="p-8 rounded-2xl bg-slate-900/50 border border-slate-800 hover:border-brand-500/30 hover:bg-slate-900 transition-all group">
                <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mb-6 group-hover:bg-brand-500/10 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      
      <footer className="border-t border-slate-800 py-12 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-600 dark:text-slate-400">
           <p>&copy; 2024 Projeto LP Híbrido. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;